import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { BeneficiaryPayoutSummary } from "@tdelivr/api-client/generated/finance/payout.js";

import { boundedNote, PER_FETCH } from "../board.ts";
import {
  BANK_DETAILS_REFUSAL,
  BOARD_NOTE,
  LISTING_NOTE,
  PAYOUTS_SCOPE,
  payoutRow,
  stateOf,
  statesPresent,
} from "../payouts.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `payouts` in `22 WP21 Merchant Portal.dc.html`: what this shop has been paid.
 *
 * **The recorded blocker was right about both of its halves, which is the first time in this
 * cohort.** It read:
 *
 * > Nothing publishes the period a payout is for — not payout.periodStart, not payout.periodEnd,
 * > not batch.periodStart — and the design's middle column is that period. FIN-07's batch listing
 * > is also the tenant's rather than this merchant's, so a merchant reading it would see every other
 * > merchant's payouts.
 *
 * Three screens in this plan had notes that were wrong about which half was missing — FIN-09's named
 * a published field, MER-02's named a published field and missed the route entirely, the support
 * console's replies board was wrong on three of four counts — so this one was re-measured expecting
 * the same, and it held. [ADR 0089](../../../docs/adr/0089-a-payout-has-a-beneficiary-and-only-the-tenant-could-read-one.md)
 * answers the second half by publishing a beneficiary-scoped listing. **The period is still absent,
 * and this board draws it as one.**
 *
 * What is drawn and what is not — `../payouts.ts` argues each:
 *
 * | drawn                            | source                                                      |
 * | -------------------------------- | ----------------------------------------------------------- |
 * | the rows                         | FIN-07 `GET /v1/merchants/{merchantId}/payouts`             |
 * | `Payout` — `PYO-0928M`           | `payoutId`, a uuid; nothing mints a short payout code       |
 * | `Period` — `22–28 Sep`           | **nothing** — no start, no end, no statement it settles     |
 * | `Net` — `KES 612,300.00`         | `amountMinor` with `currency`, in full minor units          |
 * | `human · awaiting approval`      | `state` is `CREATED`                                        |
 * | `done · paid`                    | `state` is `PAID`                                           |
 * | six more badge words             | FIN-07's other states, under the design's own classes       |
 * | `unknown · outcome unknown`      | `outcomeUncertain`, which the design has no cell for        |
 * | the four tiles                   | **not drawn** — four different absences, and R-04 for two   |
 * | `Update bank details`            | drawn and refused — `BANK_DETAILS_REFUSAL`                  |
 *
 * **The design's chip is corrected.** It reads `FIN-06 · FIN-07`, and FIN-06 is the commission
 * service — it is behind the design's `Commission this week` tile, which is not drawn, so this page
 * reads nothing from it. `tests/service-codes.test.ts` holds every code a page names to being a
 * service that page reads from, which is why the correction is in the code rather than in prose. It
 * is the second chip corrected on this portal, after `hours`'s `COM-01 · GEO`.
 *
 * **Newest first, and this is the first money board on the platform that can say so.** The finance
 * console's batch board has to state under its grid that its page is not newest-first, because a
 * batch's earliest instant moves when a payout joins it. A single payout's `requestedAt` is written
 * once and never rewritten, so the cursor walks it and the design's own order falls out — ADR 0089
 * §3.
 */

const payout = services.finance__payout;

export interface PayoutsAndStatementsProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `EAM › Business › Payouts and statements`, the design's trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Payouts and statements";
/** FIN-07, not the design's `FIN-06 · FIN-07` — see this file's head. */
const SOURCE = "FIN-07";
const DESCRIPTION = "What you have earned, what the platform kept, and when you get paid.";
const COLUMNS = ["Payout", "Period", "Net"] as const;
/** The design's own `noteK` for this page is `info`, which its `NK` map draws in the sky tint. */
const NOTE_KIND = "info" as const;
/** The design's own `FORMS.payouts[0]`, which is the word on its heading button. */
const PRIMARY = "Update bank details";

export function PayoutsAndStatements({
  transport,
  session,
  crumbs,
}: PayoutsAndStatementsProps): ReactNode {
  const [rows, setRows] = useState<readonly BeneficiaryPayoutSummary[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRows(undefined);
    setFailure(undefined);

    if (!session.scopes.has(PAYOUTS_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [PAYOUTS_SCOPE] });
      return;
    }

    /*
     * The merchant, from the token, and nothing else — the same read the three boards beside this
     * one make, and here it carries more weight than on any of them.
     *
     * FIN-07 answers an **empty page** rather than a refusal for a merchant whose payouts are not
     * this caller's, because "which merchants exist" is not a question that service can answer and a
     * 403 would confirm one exists to somebody guessing identifiers. So the thing that stops this
     * board showing another shop's money is the claim on the verified token, not a check this
     * component makes — and a missing claim is therefore refused here rather than defaulted, because
     * a board that guessed would be a board that could be made to guess correctly.
     */
    const merchantId = session.merchantId;
    if (merchantId === undefined) {
      setFailure({ kind: "forbidden", missingScopes: ["a merchant_id claim on your token"] });
      return;
    }

    try {
      const page = await payout
        .createPayoutClient(transport)
        .listMerchantPayouts(merchantId, PER_FETCH);
      setRows(page.payouts);
      setTruncated(page.nextAfter !== null);
    } catch (error) {
      setFailure(stateForError(error, [PAYOUTS_SCOPE]));
    }
  }, [transport, session.scopes, session.merchantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const boardRows =
    rows === undefined
      ? undefined
      : rows.filter((row) => state === "" || stateOf(row).label === state).map(payoutRow);

  const bounded = boundedNote(rows?.length, truncated, "payouts", "newest first");

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No payout is recorded for your shop yet. A row appears here once the platform's finance team raises one."
      primary={{ label: PRIMARY, refusal: BANK_DETAILS_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "info", text: BOARD_NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote={LISTING_NOTE}
      exportName="payouts-and-statements"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={BOARD_NOTE}
    />
  );
}
